// 簽到掃描頁面 - Material UI 版本
import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Alert,
  Snackbar,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  QrCodeScanner as QrIcon,
  Search as SearchIcon
} from '@mui/icons-material';

export default function CheckInPage({ productId, productName }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [order, setOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const scannerRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    const startScanner = async () => {
      try {
        const html5QrCode = new Html5Qrcode('qr-reader');
        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: isMobile ? 200 : 250 },
          async (decodedText) => {
            if (html5QrCode.isScanning) {
              html5QrCode.pause();
            }
            try {
              // 使用 LINE ID 取得最新訂單，並帶入活動 ID 進行簽到
              const url = productId
                ? `/api/orders/line/${decodedText}?productId=${productId}`
                : `/api/orders/line/${decodedText}`;
              const res = await fetch(url, { credentials: 'include' });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `簽到失敗（${res.status}）`);
              }
              const data = await res.json();
              setOrder(data);
              setModalVisible(true);
              
              // 根據簽到狀態顯示不同的訊息
              if (data.alreadyCheckedIn) {
                setSnackbar({
                  open: true,
                  message: `${data.message} - 簽到時間：${new Date(data.checkInTime).toLocaleString('zh-TW')}`,
                  severity: 'warning'
                });
              } else if (data.newCheckIn) {
                setSnackbar({
                  open: true,
                  message: data.message,
                  severity: 'success'
                });
              }
              
              // auto-close after 3s
              setTimeout(() => {
                setModalVisible(false);
                setOrder(null);
                if (html5QrCode.isScanning) {
                  html5QrCode.resume();
                }
              }, 3000);
            } catch (e) {
              setSnackbar({
                open: true,
                message: e.message,
                severity: 'error'
              });
              if (html5QrCode.isScanning) {
                html5QrCode.resume();
              }
            }
          },
          (errorMessage) => {
            // scanning error, ignore
          }
        );
        scannerRef.current = html5QrCode;
        setIsScanning(true);
      } catch (err) {
        setSnackbar({
          open: true,
          message: '掃描初始化失敗: ' + err,
          severity: 'error'
        });
      }
    };
    
    startScanner();
    
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {}).finally(() => {
          setIsScanning(false);
          scannerRef.current = null;
        });
      }
    };
  }, [productId, isMobile]);

  // ── 手動簽到（沒有電子票／沒有 LINE／手機打不開時用） ──
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const searchTimer = useRef(null);

  const runSearch = async (q) => {
    if (!q.trim() || !productId) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await fetch(`/api/orders/checkin/search?productId=${productId}&q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
      const data = await r.json().catch(() => []);
      if (!r.ok) throw new Error(data.error || '搜尋失敗');
      setResults(data);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSearching(false);
    }
  };

  const handleKeywordChange = (e) => {
    const v = e.target.value;
    setKeyword(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(v), 300);
  };

  const handleManualCheckIn = async (row) => {
    setCheckingId(row.orderId);
    try {
      const r = await fetch(`/api/orders/${row.orderId}/checkin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || '簽到失敗');
      setOrder(data);
      setModalVisible(true);
      setSnackbar({ open: true, message: data.message, severity: data.alreadyCheckedIn ? 'warning' : 'success' });
      setTimeout(() => { setModalVisible(false); setOrder(null); }, 3000);
      runSearch(keyword);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setCheckingId(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = () => {
    if (!order) return 'primary';
    if (order.alreadyCheckedIn) return 'warning';
    if (order.newCheckIn) return 'success';
    return 'info';
  };

  const getStatusIcon = () => {
    if (!order) return <QrIcon />;
    if (order.alreadyCheckedIn) return <WarningIcon />;
    if (order.newCheckIn) return <CheckIcon />;
    return <QrIcon />;
  };

  const getStatusText = () => {
    if (!order) return '';
    if (order.alreadyCheckedIn) {
      return `已簽到 (${new Date(order.checkInTime).toLocaleString('zh-TW')})`;
    }
    if (order.newCheckIn) return '✅ 簽到成功';
    return '簽到處理中...';
  };

  const checkInData = order ? [
    { field: '使用者', value: order.user?.name || '' },
    { field: '社團', value: order.extra?.community || '' },
    { field: '社別', value: order.extra?.department || '' },
    { field: '社職', value: order.extra?.position || '' },
    { field: '姓名', value: order.extra?.name || '' },
    { field: '座位', value: order.extra?.other || '' },
    { field: '簽到狀態', value: getStatusText() }
  ] : [];

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* 掃描器區域 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Card sx={{ maxWidth: 500, width: '100%' }} elevation={3}>
          <CardContent>
            <Box 
              id="qr-reader" 
              sx={{ 
                width: '100%', 
                maxWidth: isMobile ? 300 : 400,
                margin: '0 auto',
                '& > div': {
                  borderRadius: 2
                }
              }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* 手動簽到 */}
      {productId && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Card sx={{ maxWidth: 500, width: '100%' }} elevation={1}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>手動簽到</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                沒有電子票、沒有 LINE 或手機打不開時，用姓名／手機搜尋後簽到
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="輸入姓名、手機或 LINE 名稱"
                value={keyword}
                onChange={handleKeywordChange}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">{searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}</InputAdornment> } }}
              />
              {keyword.trim() && !searching && results.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>查無有效報名，請確認姓名或手機，或到報名管理「手動報名」</Typography>
              )}
              {results.map(r => (
                <Box key={r.orderId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                      {r.name || r.lineName}
                      {r.lineName && r.name && r.lineName !== r.name && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>LINE：{r.lineName}</Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {[r.mobile, r.department !== '一般民眾' ? r.department : r.community, r.position !== '一般民眾' ? r.position : null].filter(Boolean).join('・')}
                    </Typography>
                  </Box>
                  {r.checkInTime ? (
                    <Chip color="success" variant="outlined" size="small" label={`已簽到 ${new Date(r.checkInTime).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}`} />
                  ) : (
                    <Button variant="contained" size="small" onClick={() => handleManualCheckIn(r)} disabled={checkingId === r.orderId}
                      sx={{ minWidth: 72 }}>
                      {checkingId === r.orderId ? <CircularProgress size={16} color="inherit" /> : '簽到'}
                    </Button>
                  )}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* 簽到結果對話框 */}
      <Dialog
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            <Typography variant="h6">簽到結果</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
            <Table size="small">
              <TableBody>
                {checkInData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        width: '30%',
                        border: 'none',
                        py: 1
                      }}
                    >
                      {item.field}：
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        border: 'none',
                        py: 1,
                        color: index === checkInData.length - 1 ? 
                          (order?.alreadyCheckedIn ? 'warning.main' : 
                           order?.newCheckIn ? 'success.main' : 'text.primary') : 
                          'text.primary'
                      }}
                    >
                      {item.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          
          {order && (
            <Alert 
              severity={getStatusColor()} 
              sx={{ mt: 2 }}
              icon={getStatusIcon()}
            >
              <Typography variant="body2">
                {order.alreadyCheckedIn ? 
                  '此用戶已完成簽到，重複掃描' : 
                  order.newCheckIn ? 
                    '簽到成功！歡迎參加活動' : 
                    '簽到處理中，請稍候...'}
              </Typography>
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}